// ISDP CUSTOM: DevHub category taxonomy — the single shared vocabulary for
// "what kind of thing is this?", used by the All Documentation filter, the
// Technology Landscape sections, and the admin curation editor.
//
// There is exactly ONE category per entity and ONE list of allowed values. This
// replaced an earlier split where the Landscape had its own coarse buckets
// ('Source & CI/CD', 'Quality & Security', …) while the docs page filtered on
// free-form `tags`. Both of those are retired:
//   - the Landscape re-sections on this list,
//   - the docs page filters on this list,
//   - `tags` remain a SEARCH signal only (indexed, matched, never a filter).
//
// The order below is deliberate and owner-specified — it is the order sections
// and filter options render in. Do NOT sort alphabetically.
//
// Adding a category is a product decision, not a local one: a value outside this
// list is treated as unset (see `isDevHubCategory`), so a typo in a
// catalog-info.yaml silently drops the entry into "Uncategorised" rather than
// inventing a section.

export const DEVHUB_CATEGORIES = [
  'Architecture and Governance',
  'UX Design',
  'Security Tools',
  'Application Infrastructure',
  'Project- and Code Management',
  'Customer Interaction',
  'Mobile Development',
  'Generative AI',
  'IDEs and Coding Tools',
  'Quality and Test Tools',
  'Embedded Development',
  'Monitoring and Alerting',
  'Data Management and Integration',
  'Documentation Tools',
  'CI/CD & Automation',
  'Data & Analytics',
  'API Management & Integration',
  'Compliance & Regulatory Tools',
] as const;

export type DevHubCategory = (typeof DEVHUB_CATEGORIES)[number];

/**
 * The annotation authors set in their `catalog-info.yaml`:
 *
 *   metadata:
 *     annotations:
 *       draeger.com/category: Quality and Test Tools
 *
 * Matches the `draeger.com/*` convention used by the other author-facing
 * annotation (`draeger.com/docs-icon`). The older `isdp.io/landscape-category`
 * is still READ as a fallback so entries authored before the taxonomy merge keep
 * a section until they are migrated — see `categoryFromAnnotations`.
 */
export const ANNOTATION_CATEGORY = 'draeger.com/category';

/** Legacy, read-only fallback. Never write this. */
export const ANNOTATION_LEGACY_LANDSCAPE_CATEGORY = 'isdp.io/landscape-category';

/** Section label for entities with no (or an unrecognised) category. */
export const UNCATEGORISED = 'Uncategorised';

export function isDevHubCategory(value: unknown): value is DevHubCategory {
  return (
    typeof value === 'string' &&
    (DEVHUB_CATEGORIES as readonly string[]).includes(value)
  );
}

/**
 * Renders categories in the canonical (owner-specified) order, with anything
 * unrecognised last. Use this for section order and filter-option order so every
 * surface agrees.
 */
export function compareCategories(a: string, b: string): number {
  const ia = (DEVHUB_CATEGORIES as readonly string[]).indexOf(a);
  const ib = (DEVHUB_CATEGORIES as readonly string[]).indexOf(b);
  // -1 (unknown) must sort last, not first.
  return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
}

/**
 * The Landscape's retired coarse buckets, kept ONLY so the stored-placement re-map
 * can find rows that still hold them (`category IN (...)`) — see
 * `remapRetiredCategories` in packages/backend/src/modules/curation/curationMigrate.ts.
 * None of these is a valid category: `isDevHubCategory` rejects every one.
 */
export const RETIRED_CATEGORIES = [
  'Identity & Access',
  'Source & CI/CD',
  'Quality & Security',
  'Mobile & Distribution',
  'Cloud & Infrastructure',
  'Data & Integration',
  'Design & Brand',
  'Collaboration & Productivity',
  'Engagement & Comms',
  'Dräger Platforms & Portals',
  'Languages & Frameworks',
] as const;

/**
 * Coarse per-bucket default for a retired value, used as the CATCH-ALL second pass of
 * the re-map: rows an admin hand-typed that are not in `LANDSCAPE_SEED` and therefore
 * have no authoritative per-entity target. Seeded rows are mapped per entity from the
 * seed itself, which is finer-grained than this (e.g. 'Quality & Security' holds both
 * sonarqube → Quality and Test Tools and black-duck → Security Tools).
 *
 * DELIBERATELY OMITS 'Languages & Frameworks': it has zero seed rows and is the tech
 * radar's vocabulary leaking into the landscape, so there is no honest default for it.
 * A stray row keeps its off-list value and renders as `UNCATEGORISED` — visible, and
 * fixable by an admin — rather than being silently filed under a guess.
 */
export const RETIRED_CATEGORY_MAP: Record<string, DevHubCategory> = {
  'Identity & Access': 'Security Tools',
  'Source & CI/CD': 'CI/CD & Automation',
  'Quality & Security': 'Security Tools',
  'Mobile & Distribution': 'Mobile Development',
  'Cloud & Infrastructure': 'Application Infrastructure',
  'Data & Integration': 'Data Management and Integration',
  'Design & Brand': 'UX Design',
  'Collaboration & Productivity': 'Project- and Code Management',
  'Engagement & Comms': 'Customer Interaction',
  'Dräger Platforms & Portals': 'Customer Interaction',
};

/**
 * Resolves an entity's category from its annotations, tolerating the legacy
 * landscape annotation and trimming author whitespace. Returns `undefined` when
 * there is no usable value so callers can decide between "hide" and
 * "Uncategorised" for themselves.
 *
 * Note this deliberately does NOT map old coarse values onto new ones — that re-map
 * (`RETIRED_CATEGORY_MAP` + the seed, applied by `remapRetiredCategories` at backend
 * boot) belongs to the curation DB, which owns stored placements. Here, an
 * unrecognised value is simply unset.
 */
export function categoryFromAnnotations(
  annotations: Record<string, string> | undefined,
): DevHubCategory | undefined {
  const raw =
    annotations?.[ANNOTATION_CATEGORY] ??
    annotations?.[ANNOTATION_LEGACY_LANDSCAPE_CATEGORY];
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  return isDevHubCategory(trimmed) ? trimmed : undefined;
}
